import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';

import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import filter from 'lodash/filter';
import maxBy from 'lodash/maxBy';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import every from 'lodash/every';
import get from 'lodash/get';
import find from 'lodash/find';
import map from 'lodash/map';
import includes from 'lodash/includes';
import compact from 'lodash/compact';

import qs from 'qs';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import csvExport from '../../shared/csvExport';
import checkPermissions from '../../shared/check-permissions';
import { tableInitialState } from '../components/table';
import createFilters, { getFiltersObject } from '../../shared/filters';
import createSort from '../../shared/table/create-sort.js';

import { regions } from '../../../../../../enums/regions';

const isRocket = process.env.CPA_ENV === 'rocket';

const filters = createFilters({
	namespace: 'programsList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: compact([
		{
			name: 'locations_ids',
			title: 'Location',
			placeholder: 'Add location',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
			}
		},
		{
			name: 'regions',
			title: 'Regions',
			placeholder: 'Add regions',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'provided',
				source: regions.map(reg => ({
					value: reg,
					label: reg
				}))
			}
		},
		{
			name: 'status',
			title: 'Statuses',
			placeholder: 'Add statuses',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'static',
				source: 'root.lists.offer-statuses'
			}
		},
		{
			name: 'category',
			title: 'Categories',
			placeholder: 'Add categories',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: !isRocket ? {
				type: 'static',
				source: 'root.lists.offer-categories'
			} : {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS_CATEGORIES_RP
			}
		},
		!isRocket && ({
			name: 'niche',
			title: 'Niches',
			placeholder: 'Add niches',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.offer-niches'
			}
		}),
		{
			name: 'sale_model',
			title: 'Sale model',
			placeholder: 'Add sale model',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.offer-sale-models'
			}
		},
		{
			name: 'tags',
			title: 'Tags',
			placeholder: 'Add offer tags',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFER_TAGS
			}
		}
	])
});

const sort = createSort('programsList', 'table', {
	type: 'local',
	resolverSortFn: sortedColumn => row => {
		const value = row[sortedColumn.index];
		switch (sortedColumn.index) {
			case 'remaining_quantity_in_days':
			case 'available_quantity':
			case 'product_cap':
				return value === null ? (sortedColumn.direction === 'asc' ? Infinity : -Infinity) : parseFloat(value);
			case 'sale_model':
				return row.sale_model_title;
			case 'geo':
				return row.geo.length;
			default:
				return value;
		}
	}
});

// actions types
const TOGGLE_PROGRESS_BAR = 'programs/toggle-progress-bar';
const LOOKUP_SUCCESS = 'programs/LOOKUP_SUCCESS';
const LOOKUP_ERROR = 'programs/LOOKUP_ERROR';
const FETCH_DATA_SUCCESS = 'programs/fetch-data-success';
const FETCH_COUNTRIES_BY_REGION_SUCCESS = 'programs/fetch-countries-by-region-success';
const SET_COUNTRIES_BY_REGION = 'programs/set-countries-by-region';
const REDUEST_SUCCESS = 'programs/REDUEST_SUCCESS';
const FETCH_DATA_ERROR = 'programs/fetch-data-error';
const SHOW_MORE_ROWS = 'programs/table-show-more-rows';
const REMOVE_SUCCESS = 'programs/remove-success';
const REMOVE_ERROR = 'programs/remove-error';
const SHOW_PROGRESS_BAR = 'programs/show-progress-bar';
const CLEAR = 'programs/clear';
const SET_SEARCH_QUERY = 'programs/set-search-query';
const TOGGLE_OFFER = 'programs/toggle-offer';
const TOGGLE_OFFER_HOVER = 'programs/TOGGLE_OFFER_HOVER';
const TOGGLE_OFFERS_VIEW = 'programs/toggle-offers-view';
const FETCH_MORE = 'programs/fetch-more-success';
const REMOVE_PAGINATION = 'programs/REMOVE_PAGINATION';
const CLEAR_PAGINATION = 'programs/CLEAR_PAGINATION';
const TOGGLE_SORT_BY_VOLUME = 'programs/toggle-sort-by-volume';
const SWITCH_TO_ORDER = 'programs/switch-to-order';
const ON_ORDER_SORT_END = 'programs/on-order-sort-end';
const SAVE_ORDER_SUCCESS = 'programs/save-order-success';
const SAVE_ORDER_ERROR = 'programs/save-order-error';
const TOGGLE_COPY_OFFER_PAYOUT_POPUP = 'programs/toggle-copy-offer-payout-popup';

// permissions
export const allowedPermissions = {
	'view': 'offers_list',
	'offers_list_removed': 'offers_list_removed',
	'offers_list_export': 'offers_list_export',
	'edit': 'offers_create'
};

// initial state
const initialState = {
	data: null,
	initialListOfOffers: null,
	countriesByRegion: [],
	table: tableInitialState,
	showProgressBar: false,
	filters: filters.initialState,
	search: '',
	shouldSwitchToOrderMode: false,
	sortedOfferList: [],
	pagination: {
		limit: 150,
		offset: 0
	},
	collapseAllOffers: true,
	isSortedByVolume: false,
	isCopyOfferPayoutPopupOpen: false,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const externalQuery = (props && props.location && props.location.query && qs.parse(props.location.query)) || {};
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'programsList');

			if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
				return dispatch(replace(`programs/?${qs.stringify(savedFilters)}`));
			}
			dispatch({ type: SHOW_PROGRESS_BAR });

			dispatch(http({
				url: 'v2/offers/countries-by-region',
				method: 'get',
				actions: [FETCH_COUNTRIES_BY_REGION_SUCCESS, FETCH_DATA_ERROR]
			}));

			return request(
				getState, dispatch, externalQuery && externalQuery.query, props.route.isStoppedList
			)
				.then(action => {
					if (externalQuery.entity) {
						const filtersList = externalQuery.entity.filters || [];
						dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
					}
				});
		},
		toggleCopyOfferPayout: () => dispatch => {
			dispatch({ type: TOGGLE_COPY_OFFER_PAYOUT_POPUP });
		},
		offersLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.OFFERS}?${qs.stringify({ query: text })}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		locationsLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC}?query=${text}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		submitCopyOfferPayoutForm: (formData) => (dispatch) => {

			const request = http({
				url: `${endpoints.OFFERS.BASE}/copy-payouts`,
				method: 'post',
				data: {
					...formData,
					is_hidden_geos_included: formData.is_hidden_geos_included || '',
					location_ids: formData.location_ids && formData.location_ids.map(loc => loc.value),
					offer_from: get(formData, 'offer_from.0.value'),
					affiliate_ids: formData.affiliate_ids.split(' ').join('').split(',').map(Number),
					offer_to: get(formData, 'offer_to.0.value')
				},
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch)
				.then(action => {
					if (action.type === LOOKUP_SUCCESS) {
						dispatch(actions.toggleCopyOfferPayout());
					}
				});
		},
		exportAll: () => (dispatch, getState) => {
			return http({
				url: `${endpoints.OFFERS.EXPORT_STOCK}`,
				method: 'get',
				actions: [
					REDUEST_SUCCESS, FETCH_DATA_ERROR
				]
			})(dispatch)
				.then(action => {
					if (action.type === REDUEST_SUCCESS) {
						let rows = action.payload.data || [];
						const headers = [ 'id', 'title', 'status', 'country_name', 'stock' ];

						const columns = rows.map(entity => headers.map(h => entity[h] || '-'));
						const rowsForExport = [].concat([ headers ], columns);

						return csvExport('offers-list', rowsForExport);
					}
				});
		},
		fetchMore: () => (dispatch, getState) => {
			dispatch({ type: SHOW_MORE_ROWS });
			return request(getState, dispatch);
		},
		remove: id => dispatch => {
			const request = http({
				url: `${endpoints.OFFERS.BASE}/${id}`,
				method: 'delete',
				actions: [
					REMOVE_SUCCESS,
					REMOVE_ERROR
				]
			});

			dispatch(actions.toggleProgressBar());

			request(dispatch);
		},
		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		sortByColumn: (columnIndex) => (dispatch, getState) => {
			dispatch({ type: REMOVE_PAGINATION });

			const state = getState();

			const total = get(state.programsList.data, '[0].total_count');
			const count = get(state.programsList.data, 'length');

			if (total && total !== count) {
				dispatch(actions.toggleProgressBar());

				request(getState, dispatch)
					.then(() => dispatch(sort.actions.sortByColumn(columnIndex)));
			} else {
				dispatch(sort.actions.sortByColumn(columnIndex));
			}
		},

		showMoreRows: () => ({
			type: SHOW_MORE_ROWS
		}),

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());
			request(getState, dispatch);
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('offersSearchForm')(getState());
			const search = searchForm && searchForm.search || '';

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		showProgressBar: () => ({
			type: SHOW_PROGRESS_BAR
		}),

		apply: (props) => (dispatch, getState) => {
			dispatch(actions.clearPagination());
			dispatch(actions.showProgressBar());
			dispatch(actions.setSearchQuery());

			return request(getState, dispatch, {}, props.route.isStoppedList);
		},

		clear: () => dispatch => {
			dispatch(replace('programs/'));
			dispatch({
				type: CLEAR
			});
		},
		clearPagination: () => ({
			type: CLEAR_PAGINATION
		}),
		toggleSwitchToOrder: () => ({
			type: SWITCH_TO_ORDER
		}),

		orderSortedEnd: sorted => ({
			type: ON_ORDER_SORT_END,
			payload: sorted
		}),

		saveOrderSort: () => (dispatch, getState) => {
			const request = http({
				url: endpoints.OFFERS.SORT,
				method: 'post',
				actions: [SAVE_ORDER_SUCCESS, SAVE_ORDER_ERROR],
				data: {
					data: getState().programsList.sortedOfferList
				}
			});

			dispatch(actions.showProgressBar());

			dispatch(request).then(action => {
				if (action.type === SAVE_ORDER_SUCCESS) {
					dispatch(actions.clear());
				}
			});
		},
		toggleRow: id => dispatch => {
			dispatch({
				type: TOGGLE_OFFER,
				payload: id
			});
		},
		toggleHoverOnRow: id => dispatch => {
			dispatch({
				type: TOGGLE_OFFER_HOVER,
				payload: id
			});
		},
		toggleOffersView:() => dispatch => {
			dispatch({type: TOGGLE_OFFERS_VIEW});
		},
		sortByVolume: () => (dispatch, getState) => {
			dispatch({ type: TOGGLE_SORT_BY_VOLUME });
			return request(getState, dispatch);
		},
		setCountriesByRegion: (countriesByRegion) => (dispatch, getState) => {
			dispatch({ type: SET_COUNTRIES_BY_REGION, countriesByRegion });
		}
	},
	filters.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			if (action.payload.pathname.indexOf('programs') > -1) {
				return {
					...state,
					data: null,
					collapseAllOffers: true
				};
			}

			return initialState;
		},
		[FETCH_COUNTRIES_BY_REGION_SUCCESS]: (state, action) => ({
			...state,
			countriesByRegion: action.payload.data.map((o) => ({
				region: o.region,
				geos: o.geos.map((geo) => ({ ...geo, isActive: false })),
				isActive: false
			}))
		}),
		[SET_COUNTRIES_BY_REGION]: (state, action) => ({
			...state,
			countriesByRegion: action.countriesByRegion
		}),
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const selectedLocationsIds = map(action.meta.params.filters.locations_ids, (id) => +id);

			let data = action.payload.data;
			data = data.map(row => {
				row.geo = getGeo(row, action.meta.payoutCurrencies);
				row.languages = getLanguages(row);
				row.collapsed = true;
				if (isRocket && selectedLocationsIds.length) {
					row.domains = filter(row.domains, domain => includes(selectedLocationsIds, domain.default_country_id));
				}
				return row;
			});
			return {
				...state,
				data,
				initialListOfOffers: !state.initialListOfOffers ? data : state.initialListOfOffers,
				showProgressBar: false,
				error: false
			};
		},
		[FETCH_MORE]: (state, action) => {
			const selectedLocationsIds = map(action.meta.params.filters.locations_ids, (id) => +id);

			let data = action.payload.data;
			data = data.map(row => {
				row.geo = getGeo(row, action.meta.payoutCurrencies);
				row.languages = getLanguages(row);
				row.collapsed = true;
				if (isRocket && selectedLocationsIds.length) {
					row.domains = filter(row.domains, domain => includes(selectedLocationsIds, domain.default_country_id));
				}
				return row;
			});
			return {
				...state,
				data: state.data.concat(data),
				initialListOfOffers: !state.initialListOfOffers
					? state.data.concat(data)
					: state.initialListOfOffers.concat(data),
				showProgressBar: false,
				error: false
			};
		},
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[REMOVE_SUCCESS]: state => ({...state, data: null, showProgressBar: false }),
		[REMOVE_ERROR]: state => ({...state, error: true}),
		[REMOVE_PAGINATION]: state => ({...state, pagination: { limit: null, offset: 0 }}),
		[CLEAR_PAGINATION]: state => ({...state, pagination: {...state.pagination, ...initialState.pagination }}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[SHOW_MORE_ROWS]: state => ({
			...state,
			pagination: {
				...state.pagination,
				offset: state.pagination.offset + initialState.pagination.limit
			},
			table: {
				...state.table,
				rowsNumber: state.table.rowsNumber += initialState.table.rowsNumber
			}
		}),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload }),
		[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
		[CLEAR]: state => ({ ...initialState }),
		[SWITCH_TO_ORDER]: state => ({
			...state,
			shouldSwitchToOrderMode: !state.shouldSwitchToOrderMode
		}),
		[ON_ORDER_SORT_END]: (state, actions) => ({
			...state,
			sortedOfferList: actions.payload
		}),
		[TOGGLE_COPY_OFFER_PAYOUT_POPUP]: (state, actions) => ({
			...state,
			isCopyOfferPayoutPopupOpen: !state.isCopyOfferPayoutPopupOpen
		}),
		[SAVE_ORDER_SUCCESS]: state => ({
			...state,
			shouldSwitchToOrderMode: false,
			sortedOfferList: []
		}),
		[TOGGLE_OFFER]: (state, action) => {
			const id = action.payload;
			let data = state.data;
			data = data.map(item => {
				if (item.id === id) {
					item.collapsed = !item.collapsed;
				}
				return item;
			});
			let allOpened = every(data, {collapsed: false});
			return {
				...state,
				data,
				collapseAllOffers: !allOpened
			};
		},
		[TOGGLE_OFFER_HOVER]: (state, action) => {
			const id = action.payload;
			let data = state.data;
			data = data.map(item => {
				if (item.id === id) {
					item.is_hovered = !item.is_hovered;
				}
				return item;
			});
			return {
				...state,
				data
			};
		},
		[TOGGLE_OFFERS_VIEW]: state => {
			let data = state.data;
			data = data.map(offer => {
				offer.collapsed = !state.collapseAllOffers;
				return offer;
			});
			return {
				...state,
				data,
				collapseAllOffers: !state.collapseAllOffers
			};
		},
		[TOGGLE_SORT_BY_VOLUME]: state => ({
			...state,
			isSortedByVolume: !state.isSortedByVolume,
			showProgressBar: true
		})
	},
	filters.reducers,
	sort.reducers
);
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.programsList.data,
	getCountriesByRegion: state => state.programsList.countriesByRegion,
	getIsCopyOfferPayoutPopupOpen: state => state.programsList.isCopyOfferPayoutPopupOpen,
	getIntl: state => intl(state),
	getProgressBar: state => state.programsList.showProgressBar,
	getPayoutCurrencies: state => state.root.lists['payout-currencies'].map(currency => currency.id),
	getCan: state => checkPermissions(state, allowedPermissions),
	getTable: state => {
		const { table, data, isSortedByVolume } = state.programsList;
		const rows = data
			? !isSortedByVolume ? sort.order(data, table.sortByColumn) : data
			: [];
		return {
			headers: table.columns,
			rows: rows.slice(0, table.rowsNumber),
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getOrderOfferList: state => {
		if (!state.programsList.initialListOfOffers) return [];

		const { initialListOfOffers, filters } = state.programsList;

		const categoryFilter = find(filters.selectedFilters, filter => filter.name === 'category');
		const selectedCategoriesOptions = categoryFilter ? categoryFilter.value : [];

		const categories = selectedCategoriesOptions.map((option) => option.value);

		const offers = isRocket && categories.length
			? initialListOfOffers.filter((o) => includes(categories, o.category))
			: initialListOfOffers;

		return orderBy(offers, 'display_priority', 'desc');
	},
	getFilters: state => filters.selectors.getFilters(
		state => state.programsList.filters,
		state => state
	)(state),
	getSearchInitialValues: state => ({
		search: state.programsList.search
	}),
	getSwitchToOrderMode: state => state.programsList.shouldSwitchToOrderMode,
	getShouldShowSaveOrderBtn: state => !!state.programsList.sortedOfferList.length,
	getAllOffersCollapsed: state => state.programsList.collapseAllOffers,
	getIsSortedByVolume: state => state.programsList.isSortedByVolume,
	getError: state => state.programsList.error
};

const getGeo = (row, payoutCurrencies) => {
	return filter(row.locations, 'is_available').map(location => {
		payoutCurrencies.forEach(curr => {
			let currency = filter(row.payouts, { location_id: location.id, currency: curr });
			const avgRate = get(currency, ['0', 'avg_rate'], null);
			currency = currency && currency.length && maxBy(currency, payout => payout.amount).amount || null;
			location[`payout_${curr}`] = currency;
			location[`avg_rate_${curr}`] = avgRate;
		});

		return location;
	});
};

const getLanguages = row => {
	return row.domains && row.domains.length && row.domains.reduce((res, row) => {
		row.languages.forEach(lang => {
			if (res.indexOf(lang) === -1) {
				res.push(lang);
			}
		});
		return res;
	}, []) || [];
};

const request = (getState, dispatch, externalQuery = {}, isStoppedList = false) => {
	const state = getState();
	const { filters, search, pagination, isSortedByVolume, countriesByRegion } = state['programsList'];
	const payoutCurrencies = selectors.getPayoutCurrencies(state);

	const params = {
		search: search,
		filters: getFiltersObject(filters, false),
		inverse_filters: getFiltersObject(filters, true),
		pagination,
		order: isSortedByVolume ? { by: 'volume', d: 'desc' } : undefined,
		...externalQuery
	};

	const geos = flatten(
		filter(countriesByRegion, 'isActive').map((o) => filter(o.geos, 'isActive').map((geo) => geo.id))
	);

	if (geos.length) {
		params.filters.locations_ids = [...params.filters.locations_ids || [], ...geos];
	}

	if (isStoppedList) {
		params.is_stopped = true;
	}

	return http({
		url: `${endpoints.OFFERS.BASE}?${qs.stringify(params)}`,
		method: 'get',
		actions: [
			!pagination.offset ? FETCH_DATA_SUCCESS : FETCH_MORE,
			FETCH_DATA_ERROR
		],
		addToResponse: { params, payoutCurrencies }
	})(dispatch);
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/programs/list/index.js